import React, { useEffect, useState, useRef, useCallback } from "react";
import "../Rotas/index.css";
import { GoogleMap, useJsApiLoader, DirectionsRenderer, MarkerF } from "@react-google-maps/api";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useNavigate } from "react-router-dom";
import { useUser } from "../UserContext";

const libraries = ["places"];
const containerStyle = { width: "100%", height: "500px" };
const empresaOrigem = "RUA ANDRE NADOLNY, 1048, Colombo - PR";
const mapCenter = { lat: -25.4294, lng: -49.2719 };

const Rotas = () => {
  const navigate = useNavigate();
  const { userInfo } = useUser();

  const [dataSelecionada, setDataSelecionada] = useState("");
  const [rotasDisponiveis, setRotasDisponiveis] = useState([]);
  const [rotaSelecionada, setRotaSelecionada] = useState(null);
  const [entregas, setEntregas] = useState([]);
  const [entregasOrdenadas, setEntregasOrdenadas] = useState([]);
  const [directions, setDirections] = useState(null);
  const [infoRota, setInfoRota] = useState({ distanciaTotal: 0, tempoTotal: 0, legs: [] });
  const [buscouRotas, setBuscouRotas] = useState(false);
  const [salvandoOrdem, setSalvandoOrdem] = useState(false);
  const mapRef = useRef(null);

  // Carrega a API do Google usando a chave do Vite e monitora com o isLoaded
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries
  });

  useEffect(() => {
    if (!userInfo?.username || !userInfo?.company) {
      localStorage.removeItem('userInfo');
      navigate('/');
    }
  }, [userInfo, navigate]);

  // Evita quebra de código caso userInfo mude antes do redirecionamento
  const credentials = userInfo?.alias ? btoa(`${userInfo.alias};${userInfo.venLogin}:${userInfo.venSenha}`) : "";

  /** 🔹 Buscar rotas ao selecionar a data */
  const buscarRotas = async (data) => {
    try {
      const res = await fetch(`${userInfo.url}/rotas?ROTA_DTA_SAIDA=${data}`);
      const json = await res.json();
      setRotasDisponiveis(json);
    } catch (err) {
      console.error("Erro ao buscar rotas:", err);
      setRotasDisponiveis([]);
    } finally {
      setBuscouRotas(true);
    }
  };

  /** 🔹 Buscar entregas */
  const buscarEntregas = async (rotaId) => {
    try {
      const res = await fetch(`${userInfo.url}/entregas?ROTA_ID=${rotaId}`);
      const json = await res.json();
      setEntregas(json);
    } catch (err) {
      console.error("Erro ao buscar entregas:", err);
    }
  };

  /** 🔹 Calcular rota com opção de otimizar */
  const calcularRotaGoogle = useCallback((lista, optimize = true) => {
    // 🔹 CORREÇÃO: Se a lista estiver vazia OU o script do Google ainda não injetou o 'maps' no window, cancela a execução para não quebrar o app
    if (!lista || lista.length === 0 || !window.google?.maps) {
      setDirections(null);
      setEntregasOrdenadas([]);
      return;
    }

    const origin = empresaOrigem;
    const destinoFinal = lista[lista.length - 1].ENDERECO;
    const waypoints = lista.slice(0, -1).map((e) => ({ location: e.ENDERECO, stopover: true }));

    // Agora é 100% seguro chamar o DirectionsService
    const service = new window.google.maps.DirectionsService();
    service.route(
      {
        origin,
        destination: destinoFinal,
        waypoints,
        optimizeWaypoints: optimize,
        travelMode: window.google.maps.TravelMode.DRIVING
      },
      (result, status) => {
        if (status === "OK") {
          setDirections(result);

          let orderedList = lista;
          if (optimize) {
            const order = result.routes[0].waypoint_order.map((idx) => lista[idx]);
            orderedList = [...order, lista[lista.length - 1]];
          }

          setEntregasOrdenadas(orderedList);

          const legs = result.routes[0].legs;
          const distanciaTotal = legs.reduce((s, l) => s + l.distance.value, 0);
          const tempoTotal = legs.reduce((s, l) => s + l.duration.value, 0);
          setInfoRota({ distanciaTotal, tempoTotal, legs });
        } else {
          console.error("Erro Directions:", status);
        }
      }
    );
  }, []);

  /** 🔹 Carregar rota ao carregar entregas originais da API */
  useEffect(() => {
    if (entregas.length > 0 && isLoaded) {
      calcularRotaGoogle(entregas, true);
    } else if (entregas.length === 0) {
      setDirections(null);
      setEntregasOrdenadas([]);
    }
  }, [entregas, isLoaded, calcularRotaGoogle]);

  /** 🔹 Requisição para alterar a rota de um pedido arrastado */
  const alterarRotaPedido = async (nfeId, novaRotaId) => {
    try {
      const response = await fetch(`${userInfo.url}/nfe/rota/${nfeId}`, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json",
          "empresa": "FRIGOTESTE",
          'Authorization': `Basic ${credentials}`,
         },
        body: JSON.stringify({
          ROTA_ID: novaRotaId,
          NFE_ORDEM_CARREGA: null
        })
      });
      if (response.ok) {
        alert(`Pedido #${nfeId} movido com sucesso para a Rota #${novaRotaId}!`);
        setEntregas((prev) => prev.filter((item) => item.ID !== nfeId));
      } else {
        alert("Erro ao alterar a rota do pedido no servidor.");
      }
    } catch (error) {
      console.error("Erro no post alterarrota:", error);
    }
  };

  /** 🔹 Salvar Ordem Final de Carregamento (Pedido por Pedido) */
  const salvarOrdemCarregamento = async () => {
    if (entregasOrdenadas.length === 0) return;
    setSalvandoOrdem(true);
    let houveErro = false;

    try {
      for (let index = 0; index < entregasOrdenadas.length; index++) {
        const item = entregasOrdenadas[index];
        const ordemCarregamento = index + 1;

        const response = await fetch(`${userInfo.url}/nfe/rota/${item.ID}`, {
          method: "PUT",
          headers: { 
            "Content-Type": "application/json",
            "empresa": "FRIGOTESTE",
            'Authorization': `Basic ${credentials}`,
          },
          body: JSON.stringify({
            ROTA_ID: rotaSelecionada,
            NFE_ORDEM_CARREGA: ordemCarregamento
          })
        });

        if (!response.ok) {
          console.error(`Erro ao salvar o pedido ${item.ID}`);
          houveErro = true;
        }
      }

      if (!houveErro) {
        alert("Ordem de todas as entregas salva com sucesso!");
      } else {
        alert("Atenção: Alguns pedidos podem não ter sido salvos corretamente. Verifique o console.");
      }
    } catch (error) {
      console.error("Erro na conexão ao salvar a ordem das rotas:", error);
      alert("Erro de conexão ao salvar.");
    } finally {
      setSalvandoOrdem(false);
    }
  };

  const onDragEnd = (res) => {
    const { source, destination, draggableId } = res;
    if (!destination) return;

    if (destination.droppableId.startsWith("rota-")) {
      const novaRotaId = parseInt(destination.droppableId.replace("rota-", ""));
      const nfeId = parseInt(draggableId);
      
      if (novaRotaId === rotaSelecionada) return;

      alterarRotaPedido(nfeId, novaRotaId);
      return;
    }

    if (source.droppableId === "lista" && destination.droppableId === "lista") {
      const nova = Array.from(entregasOrdenadas);
      const [rem] = nova.splice(source.index, 1);
      nova.splice(destination.index, 0, rem);
      setEntregasOrdenadas(nova);
      calcularRotaGoogle(nova, false);
    }
  };

  const moverItem = (de, para) => {
    if (para < 0 || para >= entregasOrdenadas.length) return;
    const nova = [...entregasOrdenadas];
    const [m] = nova.splice(de, 1);
    nova.splice(para, 0, m);
    setEntregasOrdenadas(nova);
    calcularRotaGoogle(nova, false);
  };

  // 🔹 CORREÇÃO: Ajustado para gerar uma URL limpa que abre em formato de navegação GPS
  const gerarLinkGoogleMaps = (lista) => {
    const enderecos = [empresaOrigem, ...lista.map((e) => e.ENDERECO)];
    const origem = encodeURIComponent(enderecos[0]);
    const destino = encodeURIComponent(enderecos[enderecos.length - 1]);
    const waypoints = enderecos.slice(1, -1).map(encodeURIComponent).join("|");
    
    return `https://www.google.com/maps/dir/?api=1&origin=${origem}&destination=${destino}&waypoints=${waypoints}&travelmode=driving`;
  };

  // 🔹 CORREÇÃO: Garante que o app não chame o HTML antes do script do Google estar pronto
  if (!isLoaded) return <div style={{ padding: "20px" }}>Carregando mapa...</div>;

  const formatarTempo = (segundos) => {
    const totalMinutos = Math.floor(segundos / 60);
    const horas = Math.floor(totalMinutos / 60);
    const minutos = totalMinutos % 60;
    return horas > 0 ? `${horas}h ${minutos}min` : `${minutos}min`;
  };

  return (
    <div className="container">
      <h2 className="no-print">Rotas de Entrega</h2>

      <div className="select-group no-print" style={{ marginBottom: "20px" }}>
        <label>Selecione a Data: </label>
        <input
          type="date"
          value={dataSelecionada}
          onChange={(e) => {
            const val = e.target.value;
            setDataSelecionada(val);
            buscarRotas(val);
            setRotaSelecionada(null);
            setEntregas([]);
            setDirections(null);
          }}
        />
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <div className="layout-container">
          
          <div className="sidebar no-print">
            <h3>Rotas do Dia</h3>
            {buscouRotas && rotasDisponiveis.length === 0 && (
              <p className="msg-sem-rota">🚫 Sem rotas nesta data.</p>
            )}
            
            <div className="lista-rotas-sidebar">
              {rotasDisponiveis.map((r) => (
                <Droppable key={r.ROTA_ID} droppableId={`rota-${r.ROTA_ID}`}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`card-rota-sidebar ${rotaSelecionada === r.ROTA_ID ? "ativo" : ""} ${snapshot.isDraggingOver ? "dragging-over" : ""}`}
                      onClick={() => {
                        setRotaSelecionada(r.ROTA_ID);
                        buscarEntregas(r.ROTA_ID);
                        setDirections(null);
                      }}
                    >
                      <strong>Rota {r.ROTA_ID} - {r.ROTA_DESCRICAO}</strong>
                      <span className="data-rota">{new Date(r.ROTA_DTA_SAIDA).toLocaleDateString()}</span>
                      {snapshot.isDraggingOver && <div className="solte-aqui">Soltar Pedido Aqui 📥</div>}
                      <span style={{ display: "none" }}>{provided.placeholder}</span>
                    </div>
                  )}
                </Droppable>
              ))}
            </div>
          </div>

          <div className="main-content">
            {directions ? (
              <>
                <GoogleMap mapContainerStyle={containerStyle} center={mapCenter} zoom={12} onLoad={(m) => (mapRef.current = m)}>
                  <DirectionsRenderer directions={directions} options={{ suppressMarkers: true }} />
                  
                  {infoRota.legs.length > 0 && (
                    <MarkerF 
                      position={infoRota.legs[0].start_location} 
                      label={{ text: "🏭", fontSize: "16px" }}
                      title="Origem: Empresa"
                    />
                  )}

                  {infoRota.legs.map((leg, idx) => (
                    <MarkerF
                      key={`marker-${idx}`}
                      position={leg.end_location}
                      label={{
                        text: String(idx + 1),
                        color: "#ffffff",
                        fontWeight: "bold",
                        fontSize: "14px"
                      }}
                      title={`Parada ${idx + 1}: ${entregasOrdenadas[idx]?.NOME_CLIENTE || ""}`}
                    />
                  ))}
                </GoogleMap>

                <div className="info-rota">
                  <h4>Resumo da Rota Ativa</h4>
                  <span><strong>Distância:</strong> {(infoRota.distanciaTotal / 1000).toFixed(2)} km</span>
                  <span><strong>Tempo:</strong> {formatarTempo(infoRota.tempoTotal)}</span>
                  <span><strong>Pedidos:</strong> {entregasOrdenadas.length}</span>
                </div>

                <Droppable droppableId="lista">
                  {(p) => (
                    <ul className="lista-entregas" {...p.droppableProps} ref={p.innerRef}>
                      {entregasOrdenadas.map((e, i) => {
                        const trecho = infoRota.legs[i];
                        return (
                          <Draggable key={e.ID} draggableId={String(e.ID)} index={i}>
                            {(p) => (
                              <li ref={p.innerRef} {...p.draggableProps} {...p.dragHandleProps} className="item-entrega">
                                <div className="info-principal">
                                  <div className="info-esquerda">
                                    <span className="numero-ordem">{i + 1}.</span>
                                    <span className="info-entrega">
                                      <strong>Pedido:</strong> {e.ID}
                                    </span>
                                    <span className="info-entrega">
                                      <strong>Cliente:</strong> {e.NOME_CLIENTE}
                                    </span>
                                  </div>
                                  <div className="botoes-ordem no-print">
                                    {i > 0 && <button onClick={() => moverItem(i, i - 1)}>⬆️</button>}
                                    {i < entregasOrdenadas.length - 1 && <button onClick={() => moverItem(i, i + 1)}>⬇️</button>}
                                  </div>
                                </div>
                                {trecho && (
                                  <div className="info-trecho">
                                    🕒 {trecho.duration.text} – 🚗 {trecho.distance.text}
                                    <span><strong>Endereço: </strong>{e.ENDERECO}</span>
                                  </div>
                                )}
                              </li>
                            )}
                          </Draggable>
                        );
                      })}
                      {p.placeholder}
                    </ul>
                  )}
                </Droppable>

                <div className="botoes no-print">
                  <button className="btn-salvar" onClick={salvarOrdemCarregamento} disabled={salvandoOrdem}>
                    {salvandoOrdem ? "Salvando..." : "💾 Salvar Ordem de Carregamento"}
                  </button>
                  <button onClick={() => window.print()}>🖨️ Imprimir</button>
                  <button onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(gerarLinkGoogleMaps(entregasOrdenadas))}`)}>📲 WhatsApp</button>
                  <button onClick={() => window.open(`mailto:?subject=Rota&body=${encodeURIComponent(gerarLinkGoogleMaps(entregasOrdenadas))}`)}>✉️ Email</button>
                </div>
              </>
            ) : (
              rotaSelecionada && <div className="carregando-msg">Selecione uma rota ao lado ou aguarde o mapa...</div>
            )}
          </div>

        </div>
      </DragDropContext>
    </div>
  );
};

export default Rotas;